<script setup>
import { useStoreStore } from '@stores/store'
import { computed, ref, watch } from 'vue'
import _ from 'lodash'
import { useRoute } from 'vue-router'
import Button from '@components/global/button.vue'
import FaqSection from '@sections/faq-section.vue'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

const route = useRoute()
const storeStore = useStoreStore()
const selectedImage = ref(null)
const loading = ref(false)
const requestProduct = ref(null)
const product = ref({})
const slug = computed(() => route.params.slug)
const leftSlider = ref({})
const onLeftSwiper = (swiper) => {
	leftSlider.value = swiper
}

const rightSlider = ref({})
const onRightSwiper = (swiper) => {
	rightSlider.value = swiper
}

const singleVariant = computed(() => (product.value?.variants?.length > 1 ? false : true))
const optionSelections = ref({})

const formattedOptionSelections = computed(() => {
	const options = []
	for (const [key, value] of Object.entries(optionSelections.value)) {
		options.push({
			name: key,
			value: value,
		})
	}
	return options
})

const variant = computed(() => {
	return !_.isEmpty(product.value)
		? !singleVariant.value
			? (product.value.variants.filter(function (p) {
					return JSON.stringify(_.orderBy(p.selectedOptions, ['name'], ['asc'])) == JSON.stringify(_.orderBy(formattedOptionSelections.value, ['name'], ['asc']))
				})[0] ?? {})
			: product.value.variants[0]
		: {}
})

const canAddToCart = computed(() => {
	return variant.value.id ? true : false
})

function addToCart() {
	console.log('add to cart', variant.value.id)
	storeStore.addToCart({
		id: variant.value.id,
	})
}

function cancelProduct() {
	if (requestProduct.value) requestProduct.value.cancel()
}
function getProduct() {
	loading.value = true
	optionSelections.value = {}
	product.value = {}
	cancelProduct()
	let axiosSource = axios.CancelToken.source()
	requestProduct.value = { cancel: axiosSource.cancel }
	axios
		.get(`/api/store/product/${route.params.slug}`, {
			cancelToken: axiosSource.token,
		})
		.then((p) => {
			loading.value = false

			product.value = p.data

			if (product.value.images && product.value.images.length > 0) {
				selectedImage.value = product.value.images[0]
			}

			// p.data.options?.forEach(opt => {
			product.value.options?.forEach((opt) => {
				optionSelections.value[opt.name] = -1
			})
		})
		.catch((e) => {})
}
getProduct()

watch(slug, async () => {
	getProduct()
})
</script>

<template>
	<div class="relative overflow-hidden">
		<img src="https://hennep.s3.amazonaws.com/above/images/home/merch/element.svg" alt="" class="absolute -top-[80%] left-[70%] hidden w-[1200px] mix-blend-lighten md:block" />

		<img class="absolute -bottom-[70%] -left-[40%] hidden w-full rotate-[-145deg] opacity-50 mix-blend-screen md:block" src="https://hennep.s3.amazonaws.com/above/images/home/products/aurora.png" alt="" />
		<section class="container flex flex-col gap-8 uppercase">
			<div class="flex items-center justify-start gap-4 break-words font-body text-body-xs font-medium uppercase text-text-base">
				<span>merch</span>
				<i class="fas fa-chevron-right text-[10px] text-text-muted/50"></i>
				<span>{{ product?.title }}</span>
			</div>

			<div class="flex flex-col gap-8 md:flex-row">
				<!-- Desktop Image Gallery -->
				<div class="sticky top-[10rem] hidden h-fit flex-row gap-6 md:w-half xl:flex">
					<div class="flex flex-col items-center gap-4">
						<Button v-if="product.images && product.images.length > 3" @click="leftSlider.slidePrev()" class="btn-outline" :disabled="false">
							<i class="fa-solid fa-chevron-up" />
						</Button>
						<Swiper v-if="product.images && product.images.length > 1" class="max-h-[316px] w-[100px] shrink-0 grow-0 select-none" @swiper="onLeftSwiper" @slideChange="onLeftSwiper" direction="vertical" :slides-per-view="Math.min(product.images?.length || 1, 3)" :space-between="12" :allowTouchMove="false" :loop="false">
							<SwiperSlide v-for="(image, index) in product.images" :key="index" class="max-h-[96px] min-h-[96px]">
								<div @click="selectedImage = image" class="relative w-fit cursor-pointer rounded-2xl border border-sf-purple-base" :class="{ 'border-2 border-sf-purple-base': selectedImage === image }">
									<div class="w-fit overflow-hidden rounded-2xl bg-[#756AAF1A] p-3">
										<img :src="image" :alt="product.title" class="aspect-square w-[84px] object-contain" />
									</div>
								</div>
							</SwiperSlide>
						</Swiper>

						<Button v-if="product.images && product.images.length > 3" @click="leftSlider.slideNext()" class="btn-outline" :disabled="false">
							<i class="fa-solid fa-chevron-down" />
						</Button>
					</div>

					<div class="relative flex w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-sf-purple-base px-10 py-9 xl:px-40 xl:py-28">
						<svg class="absolute bottom-0 left-0 right-0 top-0" width="1000" height="1000" viewBox="0 0 1000 1000" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
							<rect width="1000" height="1000" fill="url(#pattern0_205_5986)" fill-opacity="0.7" />
							<defs>
								<pattern id="pattern0_205_5986" patternUnits="userSpaceOnUse" patternTransform="matrix(12.8 0 0 12.8 0 0)" preserveAspectRatio="none" viewBox="0 0 16 16" width="1" height="1">
									<g id="pattern0_205_5986_inner">
										<rect width="2" height="2" fill="white" fill-opacity="0.1" />
									</g>
								</pattern>
							</defs>
						</svg>

						<svg xmlns="http://www.w3.org/2000/svg" class="absolute right-5 top-5" width="10" height="10" viewBox="0 0 10 10" fill="none">
							<path d="M8 0H2.82843C1.04662 0 0.154286 2.15429 1.41422 3.41422L6.58579 8.58579C7.84572 9.84572 10 8.95338 10 7.17157V2C10 0.895431 9.10457 0 8 0Z" fill="#756AAF" />
						</svg>

						<img class="w-[360px]" :src="selectedImage || product.image" :alt="product.title" />
					</div>
				</div>

				<!-- Tablet Image Gallery (md-xl) -->
				<div class="relative hidden w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-sf-purple-base px-10 py-9 md:flex md:w-half xl:hidden xl:px-40 xl:py-28">
					<svg class="absolute bottom-0 left-0 right-0 top-0" width="1000" height="1000" viewBox="0 0 1000 1000" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
						<rect width="1000" height="1000" fill="url(#pattern0_205_5986)" fill-opacity="0.7" />
						<defs>
							<pattern id="pattern0_205_5986" patternUnits="userSpaceOnUse" patternTransform="matrix(12.8 0 0 12.8 0 0)" preserveAspectRatio="none" viewBox="0 0 16 16" width="1" height="1">
								<g id="pattern0_205_5986_inner">
									<rect width="2" height="2" fill="white" fill-opacity="0.1" />
								</g>
							</pattern>
						</defs>
					</svg>

					<svg xmlns="http://www.w3.org/2000/svg" class="absolute right-5 top-5" width="10" height="10" viewBox="0 0 10 10" fill="none">
						<path d="M8 0H2.82843C1.04662 0 0.154286 2.15429 1.41422 3.41422L6.58579 8.58579C7.84572 9.84572 10 8.95338 10 7.17157V2C10 0.895431 9.10457 0 8 0Z" fill="#756AAF" />
					</svg>

					<Swiper v-if="product.images && product.images.length > 1" @swiper="onRightSwiper" @slideChange="onRightSwiper" :modules="[Navigation]" :navigation="true" :slides-per-view="1" :allowTouchMove="true" :loop="false" class="w-[360px]">
						<SwiperSlide v-for="(image, index) in product.images" :key="index">
							<img class="w-[360px]" :src="image" :alt="product.title" />
						</SwiperSlide>
					</Swiper>
					<img v-else class="w-[360px]" :src="product.image" :alt="product.title" />
				</div>

				<!-- Mobile Image Gallery -->
				<div class="relative flex w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-sf-purple-base px-10 py-9 md:hidden xl:px-40 xl:py-28">
					<svg class="absolute bottom-0 left-0 right-0 top-0" width="1000" height="1000" viewBox="0 0 1000 1000" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
						<rect width="1000" height="1000" fill="url(#pattern0_205_5986)" fill-opacity="0.7" />
						<defs>
							<pattern id="pattern0_205_5986" patternUnits="userSpaceOnUse" patternTransform="matrix(12.8 0 0 12.8 0 0)" preserveAspectRatio="none" viewBox="0 0 16 16" width="1" height="1">
								<g id="pattern0_205_5986_inner">
									<rect width="2" height="2" fill="white" fill-opacity="0.1" />
								</g>
							</pattern>
						</defs>
					</svg>

					<svg xmlns="http://www.w3.org/2000/svg" class="absolute right-5 top-5" width="10" height="10" viewBox="0 0 10 10" fill="none">
						<path d="M8 0H2.82843C1.04662 0 0.154286 2.15429 1.41422 3.41422L6.58579 8.58579C7.84572 9.84572 10 8.95338 10 7.17157V2C10 0.895431 9.10457 0 8 0Z" fill="#756AAF" />
					</svg>

					<Swiper v-if="product.images && product.images.length > 1" @swiper="onRightSwiper" @slideChange="onRightSwiper" :modules="[Navigation]" :navigation="true" :slides-per-view="1" :allowTouchMove="true" :loop="false" class="w-[360px]">
						<SwiperSlide v-for="(image, index) in product.images" :key="index">
							<img class="w-[360px]" :src="image" :alt="product.title" />
						</SwiperSlide>
					</Swiper>
					<img v-else class="w-[360px]" :src="product.image" :alt="product.title" />
				</div>

				<div class="flex flex-1 flex-col gap-8">
					<div class="flex flex-1 flex-col gap-8">
						<div class="font-body text-body-sm font-medium text-text-base">{{ product.type }}</div>
						<h1 class="font-heading text-h2 font-medium text-sf-purple-base md:text-h1">{{ product.title }}</h1>
						<div class="w-full self-end font-body text-body-sm font-medium text-text-base md:w-half">ALL ORDERS WILL SHIP IN 3 - 5 DAYS!</div>

						<div class="flex w-full flex-wrap gap-2 font-body text-body-sm font-medium md:w-half">
							<div class="grid w-full grid-cols-2 text-text-base md:gap-3" v-if="!singleVariant">
								<div class="flex flex-col gap-2" v-for="option in product.options" :key="option.name">
									<div class="font-body text-body-sm font-medium text-text-muted/50">{{ option.name }}</div>
									<div class="isolatew-full relative">
										<select class="stext-sm w-full appearance-none rounded-full bg-bg-light px-4 py-3 font-medium normal-case outline-none placeholder:normal-case" :label="option.name" v-model="optionSelections[option.name]">
											<option value="-1" disabled>Select {{ option.name }}</option>
											<option v-for="(value, i) in option.values" :key="value" :disabled="product.options?.length == 1 && !product.variants[i].available">
												{{ value }}
												{{ product.options?.length == 1 && !product.variants[i].available ? '(Sold Out)' : '' }}
											</option>
										</select>
										<i class="fa-solid fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-base"></i>
									</div>
								</div>
							</div>
						</div>
					</div>
					<svg xmlns="http://www.w3.org/2000/svg" class="w-full" width="848" height="2" viewBox="0 0 848 2" fill="none">
						<path d="M0 1H848" stroke="#756AAF" stroke-width="1.5" stroke-dasharray="2 6" />
					</svg>
					<div class="flex flex-col justify-between gap-5 md:flex-row md:items-center">
						<h3 class="font-medium uppercase text-text-base">
							{{ product.price }}
						</h3>

						<div class="flex flex-row items-center justify-between gap-3">
							<!-- <div class="flex w-fit items-center rounded-full bg-bg-light px-1 py-2 text-text-base">
								<Button class="flex h-8 w-8 items-center justify-center disabled:opacity-30" :disabled="minusButtonDisabled" @click="changeQuantity(-1)" icon="fa-solid fa-minus" />
								<div class="flex items-center px-5">
									<span>{{ product.quantity }}</span>
								</div>
								<Button class="flex h-8 w-8 items-center justify-center disabled:opacity-30" :disabled="true" @click="changeQuantity(+1)" icon="fa-solid fa-plus" />
							</div> -->
							<Button class="btn-outline w-fit" @click="addToCart" :disabled="!canAddToCart">
								{{ product.available ? 'Add to shop Cart' : 'Sold Out' }}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</section>
		<FaqSection />
	</div>
</template>

<style scoped></style>
